import * as Yup from 'yup';
import { normalizeUrl, validateUrl } from 'src/constants/helpers';
import { emailRegex } from 'src/constants/validations';

/**
 * Every `matches` below carries this. Without it an untouched field fails both the format test and
 * `required`, and which of the two complaints reaches the screen comes down to whether the reader takes
 * the first error or the last — "EIN must be 9 digits" is the wrong thing to say about an empty field.
 */
const onlyWhenFilled = (message: string) => ({ message: message, excludeEmptyString: true });

/** The two fields the registry checks against the IRS, and the two a typo costs a rejected brand. */
export const signupBrandFieldRules = {
  legalName: Yup.string().required('Legal company name is required.'),
  // The registry accepts EIN with or without the dash; anything else is a typo worth catching here.
  ein: Yup.string()
    .matches(/^\d{2}-?\d{7}$/, onlyWhenFilled('EIN must be 9 digits, for example 12-3456789.'))
    .required('EIN is required.'),
};

export const signupAccountValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required.'),
  lastName: Yup.string().required('Last name is required.'),
  email: Yup.string()
    .matches(emailRegex, onlyWhenFilled('Invalid email format.'))
    .required('Your email is required.'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters long.')
    .max(100, 'Password must be at most 100 characters long.')
    .required('Password is required.'),
  /**
   * The server rewrites what it accepts to `+1XXXXXXXXXX`; this rule stands here so an extension or a foreign
   * number is refused on the screen that asked for it rather than by a carrier days later.
   */
  phone: Yup.string()
    .matches(
      /^(?:\+?1[\s.-]*)?\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}$/,
      onlyWhenFilled('Enter a US phone number, for example (617) 555-0100.'),
    )
    .required('Business phone is required.'),
});

// What the server accepts as a website. `URL` parses more than that: it percent-encodes spaces, non-ASCII
// and the characters below, and takes `//acme.com` and `mailto:ann@acme.com` (a user called `mailto` on the
// host `acme.com`) as addresses.
const NOT_IN_URI = /[^\x21-\x7e]|[<>"{}|\\^`[\]]/;

export const isWebsite = (value: string): boolean => {
  if (!validateUrl(value)) return false;

  const normalized = normalizeUrl(value);
  // The server's limit, counted with the scheme this adds.
  if (normalized.length > 255) return false;
  if (NOT_IN_URI.test(normalized)) return false;
  if (normalized.replace(/^https?:\/\//i, '').startsWith('/')) return false;

  const url = new URL(normalized);
  if (url.username !== '' || url.password !== '') return false;
  return url.hostname.includes('.');
};

export const signupCompanyInfoValidationSchema = Yup.object().shape({
  companyName: signupBrandFieldRules.legalName,
  // Optional like the website below.
  displayName: Yup.string().notRequired(),
  // Chosen from a list, so the only thing left to check is that one was chosen.
  entityType: Yup.string().required('Entity type is required.'),
  ein: signupBrandFieldRules.ein,
  street: Yup.string().required('Street is required.'),
  city: Yup.string().required('City is required.'),
  // Chosen from a list, so the only thing left to check is that one was chosen.
  state: Yup.string().required('State is required.'),
  // The registry takes a US ZIP and nothing else. Without this the funnel finds out at its very last step,
  // where the brand is submitted and there is no field on screen left to correct.
  postalCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, onlyWhenFilled('Postal code must be 12345 or 12345-6789.'))
    .required('Postal code is required.'),
  website: Yup.string()
    .test('website', 'Enter a website address, for example example.com.', value => !value || isWebsite(value))
    .notRequired(),
});

// For an application without an industry of its own, where the form asks the client.
export const signupCompanyInfoWithIndustryValidationSchema = signupCompanyInfoValidationSchema.shape({
  vertical: Yup.string().required('Industry is required.'),
});

export const signupCodeValidationSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{6}$/, 'The code is six digits.')
    .required('Verification code is required.'),
});
